import _ from 'underscore';
import { connect } from 'react-redux';
import { getFormValues } from 'redux-form';
import { touch } from 'redux-form';
import objectPath from 'object-path';

import moment from 'moment';
import 'moment/locale/ru';

import isEmail from 'validator/lib/isEmail';
import isURL from 'validator/lib/isURL';

const hasOwnProperty = Object.prototype.hasOwnProperty;

const validators = {
    isValid: (value, field, obj, collection) => {
        return _.isFunction(value.isValid) && value.isValid();
    },

    isNotEmpty: (value, field, obj, collection) => {
        return _.isNumber(value) && value > 0 || !_.isEmpty(value);
    },

    isNumber: (value, field, obj, collection) => {
        return !isNaN(value);
    },

    isIsNewNotEmpty: function (value, field, obj, collection) {
        return !obj.id ? true : !_.isEmpty(value);
    },

    isEmail: function (value) {
        return isEmail(value);
    },

    isURL: function (value) {
        return isURL(value);
    },

    isUnique: function (value, field, obj, collection) {
        return obj.removed !== true && !!collection && !collection.filter(function (item) {
            return item._cid != obj._cid && item.removed !== true && item[field] == value;
        }, this).length && true;
    },

    fn: function (value, field, obj, collection, fn) {
        return fn.call(this, value, field, obj, collection);
    }
};

export function validate(data, rules = {}, collection = []) {
    const results = !data.removed && _.object(_.map(_.filter(_.map(rules, function (checks, field) {
        let res = _.filter(_.map(_.pairs(checks), function (pair) {

            const validator = pair[0];
            const check = pair[1];

            if (typeof validators[validator] === 'undefined') {
                throw new Error('Unknown validator "' + validator + '"');
            }

            let res = validators[validator].apply(this, [data[field], field, data, collection, check]);

            return validator === 'fn' ? res === true ? true : [validator, res] : !res ? ([validator, _.isString(check) ? check : false]) : true;

        }, this), function (val) {
            return val !== true;
        });

        return [field, _.every(res, function (val) {
            return val === true;
        }) ? true : res];

    }), function (res) {
        return res[1].length;
    }), function (res) {
        return [res[0], _.object(res[1])];
    })) || {};

    return Object.assign({}, data, {
        _isValidated: true,
        _invalidFields: results
    });
}

// check for unique fields errors and launch revalidate callback for highlight duplicate value
// Usage: uniqueRevalidate(props, nextProps, callback);
//
/*eslint-disable */
export function uniqueRevalidate({_isValidated, _invalidFields}, {_isValidated: next_isValidated, _invalidFields: next_invalidFields}, callback) {
    if (_isValidated && next_isValidated && _invalidFields && next_invalidFields) {
        const fields = Object.keys(_invalidFields).filter((field) => Object.keys(_invalidFields[field]).indexOf('isUnique') != -1);
        const nextFields = Object.keys(next_invalidFields).filter((field) => Object.keys(next_invalidFields[field]).indexOf('isUnique') != -1);

        const diff = nextFields.filter((field) => fields.indexOf(field) == -1).length || fields.filter((field) => nextFields.indexOf(field) == -1).length;

        if (diff && _.isFunction(callback)) {
            callback();
        }
    }
}
/*eslint-enable */

export function cleanupData(data) {
    return data && (_.isArray(data) ? data.map(cleanupData) : Object.keys(data)
        .filter(key => !key.startsWith('_'))
        .reduce((obj, key) => {
            obj[key] = data[key];

            return obj;
        }, {}) || undefined);
}

export function compareByCid(item, data) {
    return item._cid === data._cid;
}

export function compareById(item, data) {
    return item.id === data.id;
}

export function compareBy(fields, predicate) {

    if (!(fields instanceof Array)) {
        fields = [fields];
    }

    let reverse = false;

    let _fields = fields.slice();
    let field = _fields.shift();

    if (!field) {
        return () => 0;
    }

    if (field[0] === '-') {
        reverse = true;
        field = field.substring(1);
    }

    return function (m, n) {

        let _m = objectPath(m).get(field);
        let _n = objectPath(n).get(field);

        if (moment.isDate(_m)) {
            const __m = moment(_m);
            if (_m && __m.isValid()) {
                _m = __m.toDate().getTime();
            }
        }

        if (moment.isDate(_n)) {
            const __n = moment(_n);

            if (_n && __n.isValid()) {
                _n = __n.toDate().getTime();
            }
        }

        if (predicate) {
            _m = predicate(_m);
            _n = predicate(_n);
        }

        if (_m === _n) {
            return compareBy(_fields)(m,n);
        } else if (_m < _n) {
            return reverse ? 1 : -1;
        } else {
            return reverse ? -1 : 1;
        }
    };
}

export function predicateDefault(data, collection) {
    return data;
}

export function findBy(collection, data, compare = compareByCid) {
    return collection.find((item) => compare(item, data));
}

export function mergeOrAppend(collection, data, compare = compareByCid, predicate = predicateDefault, ordered = true) {
    if (!findBy(collection, data, compare)) {
        const newCollection = collection.slice();
        newCollection.push(predicate(data));

        return ordered ? updateOrders(newCollection) : newCollection;
    } else {
        return merge(collection, data, compare, predicate);
    }
}

export function updateOrders(collection) {
    return collection.map((item, index) => {
        return {
            ...item,
            order: index
        };
    });
}

export function mergeOrPrepend(collection, data, compare = compareByCid, predicate = predicateDefault, ordered = true) {
    if (!findBy(collection, data, compare)) {
        const newCollection = collection.slice();
        newCollection.unshift(data);

        return ordered ? updateOrders(newCollection) : newCollection;
    } else {
        return merge(collection, data, compare, predicate);
    }
}

// Merge object to collection by comparator
//
export function merge(collection, data, compare = compareByCid, predicate = predicateDefault) {
    return collection.map((item) => compare(item, data) ? predicate(Object.assign({}, item, data), collection) : item);
}

// Merge two collections by comparator
//
export function mergeList(dst, src, compare = compareById, predicate = predicateDefault) {

    return dst.map((dstItem) => {
        return {
            ...dstItem,
            ...(findBy(src, dstItem, compare) || {})
        };
    }).concat(src.map((srcItem) => {
        return findBy(dst, srcItem, compare) ? undefined : {
            ...srcItem
        };
    }).filter(n => n))
        .map(predicate);
}

// Generate unique cid for object
//
let _cid = 0;

export function cid(data) {
    if (typeof data !== 'undefined') {
        return Object.assign({}, data, {_cid: data._cid || ++_cid});
    }

    return ++_cid;
}

export function omit(object, key) {
    const {[key]: deletedKey, ...otherKeys} = object;

    return otherKeys;
}

export function nextOrder(collection) {
    return Math.max(Math.max.apply(null, collection.map(item => item.order)), -1) + 1;
}

export function sortBy(collection, compare = '-date', predicate = undefined) {
    const newCollection = collection.slice();

    return newCollection.sort(compareBy(compare, predicate));
}

export function groupBy(collection, key) {
    return collection.reduce(function (rv, x) {
        (rv[x[key]] = rv[x[key]] || []).push(x);

        return rv;
    }, {});
};


export const normalizeBoolean = value => {
    if (value === 'true') {
        return true;
    }

    if (value === 'false') {
        return false;
    }

    return value;
};

export function connectFormState(selector) {

    return connect((state, ownProps) => {

        const _values = getFormValues(ownProps.form)(state);

        const values = Object.keys(ownProps.initialValues).reduce((obj, field) => {
            obj[field] = _values[field] || ownProps[field];

            return obj;
        }, {});

        return {
            values
        };
    });
};



// WEBPACK FOOTER //
// ./assets/app/common/utils/data.js